import { Link } from 'react-router-dom';
import { useEffect, useState, type ChangeEvent } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { AlertCircle, X } from 'lucide-react';
import {
  parseEmbedOriginsText,
  validateEmbedOrigins,
  sanitizeGalleryUrls,
  isPublicDirectorySubscriptionEligible,
  type Organization,
  type OrgAmenityKey,
} from '@court-sync/shared';
import { AcademySettingsSection } from '@/components/academy/AcademySettingsSection';
import { OrgAmenitiesSettingsSection } from '@/components/organization/OrgAmenitiesSettingsSection';
import { OrgSubdomainSettings } from '@/components/organization/OrgSubdomainSettings';
import {
  OrgWebsiteEmbedSettingsSection,
  type OrgPublicSiteSettingsFormValues,
} from '@/components/organization/OrgWebsiteEmbedSettingsSection';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Field,
  FieldLabel,
  Input,
} from '@/components/ui';
import { useSubscription } from '@/hooks/useSubscription';
import { organizationService } from '@/services/organization';
import {
  uploadOrganizationBanner,
  uploadOrganizationGalleryImage,
  uploadOrganizationLogo,
} from '@/services/storage';
import { GalleryPhotoUpload } from '@/components/GalleryPhotoUpload';
import { ACADEMY_PAGE_TOUR_STEP_IDS } from '@/lib/tour-constants';

interface OrgPublicSiteSettingsTabProps {
  organization: Organization;
  onSaved: (organization: Organization) => void;
}

export function OrgPublicSiteSettingsTab({ organization, onSaved }: OrgPublicSiteSettingsTabProps) {
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const directorySubscriptionEligible = isPublicDirectorySubscriptionEligible(
    subscription?.status
  );

  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState<string[]>(() =>
    sanitizeGalleryUrls(organization.galleryUrls ?? [], {
      bannerUrl: organization.bannerUrl,
      logoUrl: organization.logoUrl,
    })
  );
  const [amenities, setAmenities] = useState<OrgAmenityKey[]>(organization.amenities ?? []);
  const [directoryListed, setDirectoryListed] = useState(organization.directoryListed !== false);

  const showDirectorySubscriptionWarning =
    directoryListed && !subscriptionLoading && !directorySubscriptionEligible;

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<OrgPublicSiteSettingsFormValues>({
    defaultValues: {
      slug: organization.slug,
      primaryColor: organization.primaryColor ?? '#3B82F6',
      secondaryColor: organization.secondaryColor ?? '#1E40AF',
      logoUrl: organization.logoUrl ?? '',
      bannerUrl: organization.bannerUrl ?? '',
      websiteEmbedEnabled: Boolean(organization.websiteEmbedEnabled),
      embedAllowedOriginsText: (organization.embedAllowedOrigins ?? []).join('\n'),
      facebook: organization.facebook ?? '',
      instagram: organization.instagram ?? '',
      twitter: organization.twitter ?? '',
      linkedin: organization.linkedin ?? '',
      showCoachesOnPublicPage: organization.showCoachesOnPublicPage !== false,
    },
  });

  useEffect(() => {
    reset({
      slug: organization.slug,
      primaryColor: organization.primaryColor ?? '#3B82F6',
      secondaryColor: organization.secondaryColor ?? '#1E40AF',
      logoUrl: organization.logoUrl ?? '',
      bannerUrl: organization.bannerUrl ?? '',
      websiteEmbedEnabled: Boolean(organization.websiteEmbedEnabled),
      embedAllowedOriginsText: (organization.embedAllowedOrigins ?? []).join('\n'),
      facebook: organization.facebook ?? '',
      instagram: organization.instagram ?? '',
      twitter: organization.twitter ?? '',
      linkedin: organization.linkedin ?? '',
      showCoachesOnPublicPage: organization.showCoachesOnPublicPage !== false,
    });
    setGalleryUrls(
      sanitizeGalleryUrls(organization.galleryUrls ?? [], {
        bannerUrl: organization.bannerUrl,
        logoUrl: organization.logoUrl,
      })
    );
    setAmenities(organization.amenities ?? []);
    setDirectoryListed(organization.directoryListed !== false);
  }, [organization, reset]);

  const slugValue = watch('slug') || organization.slug;
  const logoUrl = watch('logoUrl');
  const bannerUrl = watch('bannerUrl');
  const primaryColor = watch('primaryColor');
  const secondaryColor = watch('secondaryColor');

  const validateImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return false;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return false;
    }
    return true;
  };

  const handleLogoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !validateImageFile(file)) return;
    try {
      setUploadingLogo(true);
      const downloadURL = await uploadOrganizationLogo(file, organization.id);
      setValue('logoUrl', downloadURL);
      setGalleryUrls((prev) =>
        sanitizeGalleryUrls(prev, { logoUrl: downloadURL, bannerUrl: watch('bannerUrl') })
      );
      toast.success('Logo uploaded');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const handleBannerUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !validateImageFile(file)) return;
    try {
      setUploadingBanner(true);
      const downloadURL = await uploadOrganizationBanner(file, organization.id);
      setValue('bannerUrl', downloadURL);
      setGalleryUrls((prev) =>
        sanitizeGalleryUrls(prev, { bannerUrl: downloadURL, logoUrl: watch('logoUrl') })
      );
      toast.success('Banner uploaded');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to upload banner');
    } finally {
      setUploadingBanner(false);
      event.target.value = '';
    }
  };

  const onSubmit = async (values: OrgPublicSiteSettingsFormValues) => {
    const parsed = parseEmbedOriginsText(values.embedAllowedOriginsText);
    const validation = validateEmbedOrigins(parsed);
    if (validation.errors.length > 0) {
      setError('embedAllowedOriginsText', { message: validation.errors[0] ?? 'Invalid embed origins' });
      return;
    }
    clearErrors('embedAllowedOriginsText');

    const res = await organizationService.updateOrganization(organization.id, {
      slug: values.slug.trim(),
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      logoUrl: values.logoUrl.trim() || null,
      bannerUrl: values.bannerUrl.trim() || null,
      galleryUrls: sanitizeGalleryUrls(galleryUrls, {
        bannerUrl: values.bannerUrl,
        logoUrl: values.logoUrl,
      }),
      websiteEmbedEnabled: values.websiteEmbedEnabled,
      embedAllowedOrigins: validation.origins,
      facebook: values.facebook.trim() || null,
      instagram: values.instagram.trim() || null,
      twitter: values.twitter.trim() || null,
      linkedin: values.linkedin.trim() || null,
      showCoachesOnPublicPage: values.showCoachesOnPublicPage,
      directoryListed,
      amenities,
    });

    if (res.success && res.data) {
      toast.success('Public site settings updated');
      onSaved(res.data);
    } else {
      toast.error(res.error || 'Failed to update public site settings');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full space-y-6">
      <input type="hidden" {...register('slug', { required: 'Slug is required' })} />
      <div id={ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_URL}>
        <OrgSubdomainSettings
          organization={organization}
          slugValue={slugValue}
          slugError={errors.slug?.message}
          onSlugChange={(slug) => setValue('slug', slug)}
          onUpdated={onSaved}
        />
      </div>

      <AcademySettingsSection title="Directory listing" id={ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_DIRECTORY}>
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <Checkbox
            id="directory-listed"
            checked={directoryListed}
            onCheckedChange={(v) => setDirectoryListed(v === true)}
            aria-describedby="directory-listed-help"
          />
          <div className="space-y-1">
            <label
              htmlFor="directory-listed"
              className="cursor-pointer text-sm font-medium text-gray-900 dark:text-white"
            >
              List in public directory
            </label>
            <p id="directory-listed-help" className="text-sm text-gray-600 dark:text-gray-400">
              When off, your organization is hidden from the CourtSync public directory search.
              Listed organizations also need an active, trialing, or past-due platform subscription.
            </p>
          </div>
        </div>
        {showDirectorySubscriptionWarning ? (
          <Alert variant="warning">
            <AlertCircle className="h-4 w-4" aria-hidden />
            <AlertTitle>Not visible in the public directory yet</AlertTitle>
            <AlertDescription>
              Directory listing is enabled, but your organization will not appear on{' '}
              <span className="font-medium">courtsync.io/directory</span> until you have an active
              platform subscription or trial.{' '}
              <Link
                to="/settings?tab=subscription"
                className="font-medium underline underline-offset-2 hover:text-yellow-900 dark:hover:text-yellow-100"
              >
                Manage subscription
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}
      </AcademySettingsSection>

      <OrgWebsiteEmbedSettingsSection
        organization={organization}
        register={register}
        control={control}
        watch={watch}
        embedOriginsError={errors.embedAllowedOriginsText?.message}
      />

      <AcademySettingsSection title="Branding & colors" id={ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_BRANDING}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="org-primaryColor">Primary color</FieldLabel>
            <div className="flex space-x-2">
              <input
                type="color"
                value={primaryColor || '#3B82F6'}
                onChange={(e) => setValue('primaryColor', e.target.value)}
                className="h-10 w-16 cursor-pointer rounded-md border border-gray-300 p-1 dark:border-gray-600"
              />
              <Input id="org-primaryColor" {...register('primaryColor')} placeholder="#3B82F6" />
            </div>
          </Field>
          <Field>
            <FieldLabel htmlFor="org-secondaryColor">Secondary color</FieldLabel>
            <div className="flex space-x-2">
              <input
                type="color"
                value={secondaryColor || '#1E40AF'}
                onChange={(e) => setValue('secondaryColor', e.target.value)}
                className="h-10 w-16 cursor-pointer rounded-md border border-gray-300 p-1 dark:border-gray-600"
              />
              <Input id="org-secondaryColor" {...register('secondaryColor')} placeholder="#1E40AF" />
            </div>
          </Field>
        </div>
      </AcademySettingsSection>

      <AcademySettingsSection title="Logo & banner images" id={ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_MEDIA}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="org-logoUrl">Logo</FieldLabel>
            <div className="space-y-2">
              <Input id="org-logoUrl" {...register('logoUrl')} placeholder="https://example.com/logo.png" />
              {logoUrl ? (
                <div className="relative aspect-square max-w-[200px] overflow-hidden rounded-md border bg-muted">
                  <img src={logoUrl} referrerPolicy="no-referrer" alt="Logo preview" className="h-full w-full object-contain p-2" />
                  <Button type="button" variant="secondary" size="sm" className="absolute right-2 top-2" onClick={() => setValue('logoUrl', '')}>
                    <X className="mr-1 h-4 w-4" aria-hidden />
                    Remove
                  </Button>
                </div>
              ) : null}
              <div className="flex items-center space-x-2">
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" id="org-logo-upload" disabled={uploadingLogo} />
                <label htmlFor="org-logo-upload" className={`cursor-pointer rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 ${uploadingLogo ? 'cursor-not-allowed opacity-50' : ''}`}>
                  {uploadingLogo ? 'Uploading…' : 'Upload logo'}
                </label>
              </div>
            </div>
          </Field>

          <Field>
            <FieldLabel htmlFor="org-bannerUrl">Banner</FieldLabel>
            <div className="space-y-2">
              <Input id="org-bannerUrl" {...register('bannerUrl')} placeholder="https://example.com/banner.png" />
              {bannerUrl ? (
                <div className="relative aspect-[3/1] max-w-md overflow-hidden rounded-md border bg-muted">
                  <img src={bannerUrl} referrerPolicy="no-referrer" alt="Banner preview" className="h-full w-full object-cover" />
                  <Button type="button" variant="secondary" size="sm" className="absolute right-2 top-2" onClick={() => setValue('bannerUrl', '')}>
                    <X className="mr-1 h-4 w-4" aria-hidden />
                    Remove
                  </Button>
                </div>
              ) : null}
              <div className="flex items-center space-x-2">
                <input type="file" accept="image/*" onChange={handleBannerUpload} className="hidden" id="org-banner-upload" disabled={uploadingBanner} />
                <label htmlFor="org-banner-upload" className={`cursor-pointer rounded-md bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 ${uploadingBanner ? 'cursor-not-allowed opacity-50' : ''}`}>
                  {uploadingBanner ? 'Uploading…' : 'Upload banner'}
                </label>
              </div>
            </div>
          </Field>
        </div>
      </AcademySettingsSection>

      <AcademySettingsSection title="Photo gallery">
        <GalleryPhotoUpload
          galleryUrls={galleryUrls}
          onChange={setGalleryUrls}
          onUpload={(file, index) => uploadOrganizationGalleryImage(file, organization.id, index)}
          inputId="org-gallery-upload"
          disabled={isSubmitting}
        />
      </AcademySettingsSection>

      <OrgAmenitiesSettingsSection selected={amenities} onChange={setAmenities} disabled={isSubmitting} />

      <AcademySettingsSection title="Social media links">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="org-facebook">Facebook</FieldLabel>
            <Input id="org-facebook" {...register('facebook')} placeholder="https://facebook.com/…" />
          </Field>
          <Field>
            <FieldLabel htmlFor="org-instagram">Instagram</FieldLabel>
            <Input id="org-instagram" {...register('instagram')} placeholder="https://instagram.com/…" />
          </Field>
          <Field>
            <FieldLabel htmlFor="org-twitter">Twitter</FieldLabel>
            <Input id="org-twitter" {...register('twitter')} placeholder="https://twitter.com/…" />
          </Field>
          <Field>
            <FieldLabel htmlFor="org-linkedin">LinkedIn</FieldLabel>
            <Input id="org-linkedin" {...register('linkedin')} placeholder="https://linkedin.com/company/…" />
          </Field>
        </div>
      </AcademySettingsSection>

      <AcademySettingsSection title="Public page sections">
        <div className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <Controller
            name="showCoachesOnPublicPage"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="showCoachesOnPublicPage"
                checked={field.value !== false}
                onCheckedChange={(v) => field.onChange(v === true)}
                aria-describedby="show-coaches-help"
              />
            )}
          />
          <div className="space-y-1">
            <label htmlFor="showCoachesOnPublicPage" className="cursor-pointer text-sm font-medium text-gray-900 dark:text-white">
              Show coaches on public site
            </label>
            <p id="show-coaches-help" className="text-sm text-gray-600 dark:text-gray-400">
              When off, the &quot;Our Coaches&quot; section is hidden from your public landing page.
            </p>
          </div>
        </div>
      </AcademySettingsSection>

      <div
        id={ACADEMY_PAGE_TOUR_STEP_IDS.PUBLIC_SITE_SAVE}
        className="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700"
      >
        <Button type="submit" isLoading={isSubmitting}>
          Save public site settings
        </Button>
      </div>
    </form>
  );
}
